import { db } from '../config/firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { checkUnprocessedSales, checkPendingPayments, checkLowStockProducts, checkSalesGoals, checkNewQuoteRequests, checkNewMessages } from '../utils/cronJobs/notificationChecks';

export interface CronJob {
  name: string;
  interval: number; // in milliseconds
  lastRun?: Date;
  nextRun?: Date;
  status: 'pending' | 'running' | 'completed' | 'failed';
  error?: string;
  fn: () => Promise<void>;
}

const cronJobs: CronJob[] = [
  {
    name: 'Verificar Vendas Não Processadas',
    interval: 1800000, // 30 minutos
    fn: checkUnprocessedSales
  },
  {
    name: 'Verificar Pagamentos Pendentes',
    interval: 3600000, // 1 hora
    fn: checkPendingPayments
  },
  {
    name: 'Verificar Produtos com Estoque Baixo',
    interval: 7200000, // 2 horas
    fn: checkLowStockProducts
  },
  {
    name: 'Verificar Metas de Vendas',
    interval: 86400000, // 24 horas
    fn: checkSalesGoals
  },
  {
    name: 'Verificar Novos Pedidos de Cotação',
    interval: 900000, // 15 minutos
    fn: checkNewQuoteRequests
  },
  {
    name: 'Verificar Novas Mensagens',
    interval: 300000, // 5 minutos
    fn: checkNewMessages
  }
].map(job => ({
  ...job,
  status: 'pending' as const,
  lastRun: undefined,
  nextRun: new Date(Date.now() + job.interval)
}));

async function updateJobStatus(jobName: string, status: CronJob['status'], error?: string) {
  try {
    const jobsRef = collection(db, 'cronJobs');
    const now = Timestamp.now();
    const data = {
      name: jobName,
      status,
      lastRun: now,
      nextRun: new Timestamp(now.seconds + Math.floor(cronJobs.find(j => j.name === jobName)?.interval! / 1000), 0),
      ...(error && { error })
    };

    await addDoc(jobsRef, data);
  } catch (err) {
    console.error(`Erro crítico ao atualizar status do job ${jobName}:`, err);
  }
}

export async function runJob(job: CronJob) {
  try {
    // Só loga quando a tarefa inicia ou falha
    if (job.status === 'pending') {
      await updateJobStatus(job.name, 'running');
    }
    await job.fn();
    job.status = 'completed';
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
    console.error(`Erro crítico ao executar job ${job.name}:`, errorMessage);
    await updateJobStatus(job.name, 'failed', errorMessage);
  }
}

export function initCronJobs() {
  cronJobs.forEach(job => {
    const run = async () => {
      try {
        await runJob(job);
        job.lastRun = new Date();
        job.nextRun = new Date(Date.now() + job.interval);
        setTimeout(run, job.interval);
      } catch (error) {
        console.error(`Erro crítico no ciclo do job ${job.name}:`, error);
        setTimeout(run, job.interval);
      }
    };
    setTimeout(run, 0);
  });
}

export function getCronJobs() {
  return cronJobs;
}
